import { message, Tabs, TabsProps } from 'antd';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TEXT_BUTTON_HEADER_LIST } from '../../../config/config';
import { PermissionCategories } from '../../../permissions/PermissionCategories';
import { PermissionProducts } from '../../../permissions/PermissionProducts';
import { PermissionProperties } from '../../../permissions/PermissionProperties';
import authStore from '../../../stores/authStore';
import categoryStore from '../../../stores/categoryStore';
import productStore from '../../../stores/productStore';
import propertiesStore from '../../../stores/propertiesStore';
import unsavedStore from '../../../stores/unsavedStore';
import BreadCrumbComponent from '../../atoms/BreadCrumbComponent';
import ActionButton from '../../atoms/ButtonComponent';
import NotPermissionComponent from '../../atoms/NotPermissionComponent';
import ContentBox from '../../molecules/ContentBox';
import LoadingComponent from '../../molecules/LoadingComponent';
import Permission from '../../molecules/Permission';
import TableSpecification from '../../organisms/ProductComponents/TableSpecification';
import UplodFileProduct from '../../organisms/ProductComponents/UplodFileProduct';
import { useAuth } from '../../protected/ProviderAuth';
import ProdutoInformation from './ProdutoInformation';
import { validationSchema } from './schema';
import DescriptionProduct from '../../organisms/ProductComponents/DescriptionProduct';

const breadCrumb = [
    {
        title: <Link to="/dashboard">Dashboard</Link>,
    },
    {
        title: <Link to="/produtos">Listagem de Produtos</Link>,
    },
    {
        title: <p>Cadastro de Produto</p>,
    },
]

const CreateProduct: React.FC = observer(() => {
    const { uuid } = useParams<{ uuid: string }>();
    const { product } = productStore;
    const navigate = useNavigate();
    const { merchant } = authStore;
    const [activeTabKey, setActiveTabKey] = useState('1');
    const [loading, setLoading] = useState(true);
    const { hasPermission } = useAuth();
    const [price, setPrice] = useState<String>();
    const [stockProduct, setStockProduct] = useState<number>();
    const [active, setActive] = useState(false);
    const [fakePrice, setFakePrice] = useState<String>();

    useEffect(() => {
        productStore.reset();
        const getInformation = async () => {
            setLoading(true);
            setPrice(undefined);

            if (uuid && hasPermission(PermissionProducts.GET_UUID)) {
                const response = await productStore.getProductByUuid(uuid);

                if (response.prices.length > 0) {
                    setPrice(response?.prices[0]?.price);
                }

                setFakePrice(response.fakePrice);

                if (response.stocks.length > 0) {
                    setStockProduct(response?.stocks[0]?.total);
                }

                if (!response.error) {
                    setActive(product.active);
                }
            }

            if (hasPermission(PermissionProperties.GET)) {
                await propertiesStore.getList({ size: 999999, page: 0, sort: 'created,desc' });
            }

            if (hasPermission(PermissionCategories.GET)) {
                await categoryStore.getList();
            }
            setLoading(false);
        };

        getInformation();

        return () => {
            // Remover ou comentar isso se você não quiser resetar ao desmontar
            productStore.reset();
            categoryStore.reset();
        };
    }, [uuid]);


    const initialValues = {
        active: product?.active || false,
        name: product?.name || '',
        shortName: product?.shortName || '',
        barcode: product?.barcode || '',
        videoPath: product?.videoPath || '',
        slug: product?.slug || '',
        skuCode: product?.skuCode || '',
        metaCanonical: product?.metaCanonical || '',
        metaKeywords: product?.metaKeywords || '',
        metaTitle: product?.metaTitle || '',
        metaDescription: product?.metaDescription || '',
        shortDescription: product?.shortDescription || '',
        description: product?.description || '',

        //Infomação de Ficha Tecnica
        netWeight: product?.netWeight || '',
        shippingLength: product?.shippingLength || '',
        weight: product?.weight || '',
        height: product?.height || '',
        length: product?.length || '',
        shippingWeight: product?.shippingWeight || '',
        shippingHeight: product?.shippingHeight || '',
        shippingWidth: product?.shippingWidth || '',
        width: product?.width || '',
        cubage: product?.cubage || '',
        ordering: '',
        path: '',
        categories: product?.categories,
        price: product?.prices && product?.prices[0]?.price,
        fakePrice: product?.fakePrice,
        stocks: product?.stocks && product?.stocks[0]?.total,
    };

    function parseBrazilianPrice(str: string | number): number {
        if (typeof str === 'number') return str;
        return parseFloat(str.replace(/\./g, '').replace(',', '.'));
    }

    function formatName(name: string) {
        return name.toLowerCase().trim().replace(/\s+/g, '-');
    }

    const handleSubmit = async (values: typeof initialValues, formikHelpers: FormikHelpers<typeof initialValues>) => {
        setLoading(true);
        let response;

        const createProps = {
            ...product,
            ...values,
            currency: "BRL",
            merchant,
            //@ts-ignore
            fakePrice: parseBrazilianPrice(fakePrice),
            slug: product?.slug
                ? formatName(product.slug)
                : formatName(values?.name ?? ""),
            price: null,
            prices: values?.price !== undefined
                ? [
                    {
                        ...product?.prices?.[0],
                        '@class': 'br.com.stoom.kernel.model.persistence.Price',
                        price: parseBrazilianPrice(values.price),
                    }
                ]
                : undefined,
            stocks:
                values?.stocks !== undefined && values?.stocks !== null
                    ? [
                        {
                            ...product?.stocks?.[0],
                            total: values.stocks,
                        }
                    ]
                    : undefined,
        };

        if (uuid) {
            response = await productStore.editProduct(createProps, uuid);
        } else {
            response = await productStore.createProduct(createProps);
        }

        setLoading(false);

        if (!response.error) listRouter();
    };

    const listRouter = () => {
        navigate(`/produtos`);
    }

    const onChange = (key: string) => {
        setActiveTabKey(key);
    };

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: 'Informação Geral',
            children: <ProdutoInformation fakePrice={fakePrice} setFakePrice={setFakePrice} setActive={setActive} active={active} setPrice={setPrice} price={price} setStockProduct={setStockProduct} stockProduct={stockProduct} />,
        },
        {
            key: '2',
            label: 'Imagens',
            children: <UplodFileProduct setActive={setActive} active={active} />,
        },
        {
            key: '3',
            label: 'Especificação',
            children: <TableSpecification />,
        },
        {
            key: '4',
            label: 'Descrição Completa',
            children: <DescriptionProduct />,
        },
    ];

    return (
        <>
            <BreadCrumbComponent breadCrumbList={breadCrumb} permission={PermissionProducts.GET} textButton={TEXT_BUTTON_HEADER_LIST} onClick={() => listRouter()} />

            {loading ? (
                <ContentBox className="mb-4 p-6" title="Cadastro de Produto" description="Tela para cadastro de Produto">
                    <LoadingComponent />
                </ContentBox>
            ) : (
                <Permission
                    permissionKeys={[PermissionProducts.POST, PermissionProducts.PUT, PermissionProducts.GET_UUID]}
                    fallback={NotPermissionComponent()}
                >
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        enableReinitialize
                        validationSchema={validationSchema}
                    >
                        {({ validateForm, dirty }) => {
                            useEffect(() => {
                                unsavedStore.updateUnsavedChanges(dirty);
                            }, [dirty]);

                            return (
                                <Form>
                                    <Tabs
                                        className="mb-0"
                                        type="card"
                                        activeKey={activeTabKey}
                                        onChange={onChange}
                                        items={items}
                                    />
                                    <div className="flex justify-end">
                                        <Permission permissionKeys={[PermissionProducts.PUT, PermissionProducts.POST]} buttonType>
                                            <ActionButton onClick={async () => {
                                                // Valida o formulário e atualiza o objeto errors
                                                const formErrors = await validateForm();

                                                if (Object.keys(formErrors).length > 0) {
                                                    setActiveTabKey('1'); // Redireciona para a primeira aba
                                                    message.error(`Há campos que não foram preenchidos`);
                                                }
                                            }} htmlType="submit" actionType="primary">{uuid ? 'Atualizar' : 'Cadastrar'}</ActionButton>
                                        </Permission>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </Permission>
            )}
        </>
    );
});

export default CreateProduct;